import { useCallback, useEffect, useState } from 'react';

import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import FormFieldset from '@/components/atoms/Fieldset/templates/FormFieldset';
import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import { Switch } from '@/components/ui/switch';
import { useIntegrations } from '@/hooks/useIntegrations';
import { getPermissions } from '@/redux/permissions/selectors';
import { Integration } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { timeFromNow } from '@/util/dateUtils';

import { FieldsetProps } from '../../Fieldset';

type Form = { active: boolean };
interface Props extends FieldsetProps {
  integration: Integration;
}

const ActiveFieldset = ({ integration, ...fieldsetProps }: Props) => {
  const { t } = useTranslation();
  const integration_id = integration?.integration_id;
  const desk_id = integration?.desk_id;
  const [lastActive, setLastActive] = useState(integration?.last_session_at);
  const [integrationActive, setIntegrationActive] = useState(integration?.active);

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );

  useEffect(() => {
    setLastActive(integration?.last_session_at);
  }, [integration?.last_session_at]);

  useEffect(() => {
    setIntegrationActive(integration?.active);
  }, [integration?.active]);

  const { control, reset, handleSubmit } = useForm<Form>({
    defaultValues: { active: integration?.active ?? false },
  });

  const active = useWatch({ control, name: 'active' });

  const { updateIntegration, updateStatus } = useIntegrations(desk_id, integration_id);

  useEffect(() => {
    if (integration_id) {
      reset({ active: integration?.active ?? false });
    }
  }, [integration?.active, integration_id, reset]);

  const onSubmit = useCallback(
    (form) => {
      updateIntegration({ ...integration, config: { ...integration?.config }, ...form });
    },
    [updateIntegration, integration]
  );
  return (
    <FormFieldset
      title={fieldsetProps.title}
      subtitle={fieldsetProps.subtitle}
      primaryButton={{
        children: t('common.save'),
        disabled: integration?.active === active || !canWrite,
        isLoading: updateStatus === 'pending',
      }}
      onSubmit={handleSubmit(onSubmit)}
      isLoading={!integration}
      helpText={t('integrations.fieldsets.active_fieldset.helpText')}
      hasBadge={true}
      integrationActive={integrationActive}
    >
      <Controller
        render={({ field: { onChange, value } }) => {
          return (
            <Switch
              label={t('integrations.fieldsets.active_fieldset.active')}
              checked={value}
              disabled={!integration || !canWrite}
              onCheckedChange={onChange}
            />
          );
        }}
        name="active"
        control={control}
      />

      {lastActive && (
        <div className="flex pt-6 text-[10px] leading-[18px] font-semibold uppercase">
          <span className="pr-1">{t('integrations.fieldsets.active_fieldset.last_session')}:</span>
          <TimestampTooltip timestamp={lastActive}>
            <span className="text-informational">{timeFromNow(lastActive)}</span>
          </TimestampTooltip>
        </div>
      )}
    </FormFieldset>
  );
};

export default ActiveFieldset;
